import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SeverityBadge } from "@/components/SeverityBadge";
import { Brain, Pulse, Cpu } from "@phosphor-icons/react";

export default function MLDetection() {
  const [models, setModels] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    const [m, a, s] = await Promise.all([api.get("/ml/models"), api.get("/ml/anomalies"), api.get("/ml/stats")]);
    setModels(m.data.models); setAnomalies(a.data.anomalies); setStats(s.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="ml-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Anomaly Detection Engine</p>
        <h1 className="font-head text-3xl tracking-tighter font-black mt-1">ML Detection</h1>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[["Active Models", stats.models_active], ["Anomalies", stats.anomalies_detected], ["High Confidence", stats.high_confidence], ["Avg Accuracy", `${(stats.avg_accuracy * 100).toFixed(1)}%`]].map(([l, v]) => (
            <div key={l} className="bg-[#0C0C14] border border-[#1F1F33] p-4">
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#71717A]">{l}</div>
              <div className="font-head text-3xl font-black mt-1 text-[#00F0FF]">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 space-y-3" data-testid="ml-models">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717A] mb-1">Detection Models</div>
          {models.map((m) => (
            <div key={m.id} className="bg-[#0C0C14] border border-[#1F1F33] p-4">
              <div className="flex items-center gap-2">
                <Cpu size={16} weight="duotone" color="#00F0FF" />
                <span className="font-head font-bold text-sm">{m.name}</span>
                <span className="ml-auto text-[9px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ color: m.status === "active" ? "#3ECF8E" : "#FFCC00", borderColor: "#1F1F33" }}>{m.status}</span>
              </div>
              <div className="text-[11px] text-[#71717A] mt-1">{m.task}</div>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 bg-[#1F1F33]"><div className="h-full bg-[#00F0FF]" style={{ width: `${m.accuracy * 100}%` }} /></div>
                <span className="text-[10px] text-[#A1A1AA] tabular-nums">{(m.accuracy * 100).toFixed(1)}%</span>
              </div>
            </div>
          ))}
        </div>

        <div className="xl:col-span-2">
          <div className="flex items-center gap-2 mb-2"><Pulse size={16} color="#FF3B30" /><span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Detected Anomalies</span></div>
          <div className="bg-[#0C0C14] border border-[#1F1F33]" data-testid="ml-anomalies">
            {anomalies.map((a) => (
              <div key={a.id} data-testid={`anomaly-${a.id}`} className="flex items-center gap-4 px-4 py-3 border-b border-[#1F1F33]">
                <div className="w-24 shrink-0"><SeverityBadge level={a.severity} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate">{a.description}</div>
                  <div className="text-[11px] text-[#71717A]">{a.host} · {a.model}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm" style={{ color: a.anomaly_score >= 0.9 ? "#FF3B30" : a.anomaly_score >= 0.8 ? "#FF9500" : "#FFCC00" }}>{a.anomaly_score}</div>
                  <div className="text-[9px] uppercase text-[#71717A]">score</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
